import { useMemo, useState } from "react";
import { Activity } from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CrmPetDiagnosisController } from "../../../../services/controllers/crmPetDiagnosis";
import { useCrmPetDiagnosis } from "../../../../store/crmPetDiagnosis/useCrmPetDiagnosis";
import { aggregateUsageByViewMode } from "../helpers";
import { ActivityTableRow } from "./ActivityTableRow";
import { EmptyState } from "../components/EmptyState";
import { SectionCard } from "../components/SectionCard";
import { SectionError } from "../components/SectionError";

const VIEW_MODES = [
  { value: "daily", label: "Diária", columnLabel: "Data", title: "Atividade Diária" },
  { value: "weekly", label: "Semanal", columnLabel: "Semana", title: "Atividade Semanal" },
  { value: "monthly", label: "Mensal", columnLabel: "Mês", title: "Atividade Mensal" },
];

export function ActivityChart() {
  const controller = CrmPetDiagnosisController();
  const daily = useCrmPetDiagnosis((state) => state.data.usage.daily);
  const loading = useCrmPetDiagnosis((state) => state.data.loading.usage);
  const error = useCrmPetDiagnosis((state) => state.data.errors.usage);
  const [viewMode, setViewMode] = useState("daily");

  const activeViewMode = VIEW_MODES.find((mode) => mode.value === viewMode) || VIEW_MODES[0];
  const chartData = useMemo(
    () => aggregateUsageByViewMode(daily, viewMode),
    [daily, viewMode],
  );

  return (
    <SectionCard
      title={activeViewMode.title}
      description="Os dados podem ser visualizados por dia, semana ou mês, agrupando oportunidades, pendências, contatos e interações do período."
      icon={Activity}
      actions={
        <div className="flex flex-wrap gap-2 self-center ml-auto">
          {VIEW_MODES.map((mode) => (
            <button
              key={mode.value}
              type="button"
              aria-pressed={viewMode === mode.value}
              className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${viewMode === mode.value
                ? "border-scale-500 bg-scale-500 text-background"
                : "border-gray-300 bg-background text-gray-700 hover:border-scale-400"
                }`}
              onClick={() => setViewMode(mode.value)}
            >
              {mode.label}
            </button>
          ))}
        </div>
      }
    >



      {error && <SectionError message={error} onRetry={() => controller.retrySection("usage")} />}
      {!error && loading && <div className="h-[320px] animate-pulse rounded-md bg-gray-100" />}
      {!error && !loading && !daily.length && (
        <EmptyState
          title="Sem consolidação de atividade"
          description="Ainda não existem dados de utilização para o período selecionado."
        />
      )}

      {!error && !loading && daily.length > 0 && (
        <>
          <div className="h-[320px] w-full" aria-label="Gráfico da atividade diária do CRM Pet">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} className="stroke-gray-200" />
                <XAxis dataKey="label" stroke="var(--color-gray-500)" />
                <YAxis stroke="var(--color-gray-500)" allowDecimals={false} />
                <ChartTooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="eligibleOpportunities"
                  name="Oportunidades atuais"
                  stroke="#67a56e"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="pendingContacts"
                  name="Pendentes"
                  stroke="var(--color-warning)"
                  strokeWidth={2}
                />
                <Bar
                  dataKey="contactsMade"
                  name="Contatos realizados"
                  fill="var(--color-scale-500)"
                  maxBarSize={24}
                />
                <Bar
                  dataKey="views"
                  name="Interações"
                  fill="var(--color-scale-700)"
                  maxBarSize={24}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <details className="mt-4 text-sm">
            <summary className="cursor-pointer font-semibold text-scale-500">
              Ver dados do gráfico em tabela
            </summary>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[620px] border-collapse">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">{activeViewMode.columnLabel}</th>
                    <th className="px-3 py-2 text-right">Oportunidades</th>
                    <th className="px-3 py-2 text-right">Pendentes</th>
                    <th className="px-3 py-2 text-right">Contatos</th>
                    <th className="px-3 py-2 text-right">Interações</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((item) => (
                    <ActivityTableRow key={item.key} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </SectionCard>
  );
}
